import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import cx from 'classnames';
import Constants from 'config/Constants';
import { insert, move } from 'formik';
import { cloneDeep } from 'lodash';
import { ApprovalParticipationModalListener } from 'pages/approvals/modals/ApprovalParticipationModalListener';
import { useCanAddNewBlocks } from 'pages/documents_v2/hooks/useCanAddNewBlocks';
import { useGetDocument } from 'pages/documents_v2/hooks/useGetDocument';
import { AddBlock } from 'pages/documents_v2/views/edit_area/AddBlock';
import { BlockVariants } from 'pages/documents_v2/views/edit_area/BlockVariants';
import { AreaContext } from 'pages/documents_v2/views/edit_area/context/AreaContext';
import { EditAreaContext } from 'pages/documents_v2/views/edit_area/context/EditAreaContext';
import { EmptyAreaPlaceholder } from 'pages/documents_v2/views/edit_area/EmptyAreaPlaceholder';
import { useGetBlockApprovalParticipation } from 'pages/documents_v2/views/edit_area/hooks/useGetBlockApprovalParticipation';
import { useIsReadOnly } from 'pages/documents_v2/views/edit_area/hooks/useIsReadOnly';
import { InsertBlockField } from 'pages/documents_v2/views/edit_area/InsertBlockField';
import { findBlockDataFromArray, formatData, parseBlockData } from 'pages/global/BlockLayoutHelper';
import { useContext } from 'react';
import { BooleanParam, NumberParam, useQueryParam } from 'use-query-params';

import HelperFunctions from '../../../global/HelperFunctions';
import { BlockEditModals } from './BlockEditModals';
import { Block } from './blocks/Block';

export function AreaBlocks({ area, areaId, areaBlocks = [], blocksToRender = [], canMoveBlocks = true }) {
    const { draggedItemKey, setDraggedItemKey } = useContext(AreaContext);
    const { addBlock, updateBlock, replaceBlocks } = useContext(EditAreaContext);
    const canAddNewBlocks = useCanAddNewBlocks(area);
    const document = useGetDocument();

    const showEmptyAreaPlaceholder = !!(blocksToRender.length === 0 && canAddNewBlocks);
    const areaIsTemplate = area?.properties?.isTemplate === true;
    const documentIsTemplate = document?.type === Constants.documentType.model;
    const documentIsLibrary = document?.type === Constants.documentType.library;

    return (
        <>
            {area && canAddNewBlocks && <AddBlock area={area} />}

            <DragDropContext
                onBeforeDragStart={({ draggableId }) => setDraggedItemKey(parseInt(draggableId))}
                onDragEnd={onDragEnd}
                isDropDisabled={canMoveBlocks === false}
            >
                <Droppable type="BLOCK" key={`droppable-area-${areaId}`} droppableId={`area|${areaId}`}>
                    {(provided, snapshot) => (
                        <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            style={getListStyle(snapshot.isDraggingOver)}
                            className={cx('edit-area-blocks-wrapper', {
                                'is-dragging': draggedItemKey !== undefined,
                                'area-is-template': areaIsTemplate,
                                'document-is-template': documentIsTemplate,
                                'document-is-library': documentIsLibrary,
                            })}
                        >
                            <RenderAreaBlocks
                                area={area}
                                blocksToRender={blocksToRender}
                                isDraggable={canMoveBlocks}
                                areaBlocks={areaBlocks}
                                canAddNewBlocks={canAddNewBlocks}
                            />
                            {provided.placeholder}

                            {showEmptyAreaPlaceholder && (
                                <div className="edit-area-blocks dr-container-body last pb-4">
                                    <EmptyAreaPlaceholder addBlock={addBlock} />
                                </div>
                            )}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>

            <BlockEditModals />
            <ApprovalParticipationModalListener />
        </>
    );

    function handleBlockLayoutDrag(result) {
        let { source, destination, draggableId } = result;

        const sourceIndex = source.droppableId.split('|');
        const sourceBlockKey = +sourceIndex[1];
        const sourceRowIndex = +sourceIndex[2];
        const sourceColIndex = +sourceIndex[3];

        source.blockKey = sourceBlockKey;
        source.rowIndex = sourceRowIndex;
        source.colIndex = sourceColIndex;

        const destinationIndex = destination.droppableId.split('|');
        const destinationBlockKey = +destinationIndex[1];
        const destinationRowIndex = +destinationIndex[2];
        const destinationColIndex = +destinationIndex[3];

        destination.blockKey = destinationBlockKey;
        destination.rowIndex = destinationRowIndex;
        destination.colIndex = destinationColIndex;

        // Block new in the layout?
        if (source.droppableId.startsWith('area|')) {
            const currentLayoutBlockContent = findBlockDataFromArray(destinationBlockKey, areaBlocks);
            const currentData = currentLayoutBlockContent.data;

            currentData[destinationRowIndex][destinationColIndex] = insertBlockLayout(
                currentData[destinationRowIndex][destinationColIndex],
                destination.index,
                {
                    id: parseInt(draggableId),
                },
            );

            const newContent = formatData(currentData);
            const blockIndex = areaBlocks.findIndex((block) => block.key === destinationBlockKey);

            if (blockIndex >= 0) {
                updateBlock(parseBlockData(newContent), blockIndex);
            }

            return;
        }

        // Block moved within the same cell?
        if (
            sourceBlockKey === destinationBlockKey &&
            sourceRowIndex === destinationRowIndex &&
            sourceColIndex === destinationColIndex
        ) {
            const currentLayoutBlockContent = findBlockDataFromArray(sourceBlockKey, areaBlocks);
            const currentData = currentLayoutBlockContent.data;

            currentData[sourceRowIndex][sourceColIndex] = reorderBlockLayout(
                currentData[sourceRowIndex][sourceColIndex],
                source.index,
                destination.index,
            );

            const newContent = formatData(currentData);
            const blockIndex = areaBlocks.findIndex((block) => block.key === destinationBlockKey);

            if (blockIndex >= 0) {
                updateBlock(parseBlockData(newContent), blockIndex);
            }

            return;
        }

        // Block moved another cell
        const currentBlockContent = findBlockDataFromArray(sourceBlockKey, areaBlocks);
        const currentData = currentBlockContent.data;

        const items = moveBlockLayout(
            currentData[sourceRowIndex][sourceColIndex],
            currentData[destinationRowIndex][destinationColIndex],
            source,
            destination,
        );

        currentData[sourceRowIndex][sourceColIndex] = items[0];
        currentData[destinationRowIndex][destinationColIndex] = items[1];

        const newContent = formatData(currentData);
        const blockIndex = areaBlocks.findIndex((block) => block.key === destinationBlockKey);

        if (blockIndex >= 0) {
            updateBlock(parseBlockData(newContent), blockIndex);
        }
    }

    function removeBlockFromLayout(result) {
        let { source, destination, draggableId } = result;

        const sourceIndex = source.droppableId.split('|');
        const sourceBlockKey = +sourceIndex[1];
        const sourceRowIndex = +sourceIndex[2];
        const sourceColIndex = +sourceIndex[3];

        const currentLayoutBlockContent = findBlockDataFromArray(sourceBlockKey, areaBlocks);

        const newSourceState = currentLayoutBlockContent.data;
        removeBlockLayout(newSourceState[sourceRowIndex][sourceColIndex], source.index);

        const newContent = formatData(newSourceState);
        const blockIndex = areaBlocks.findIndex((block) => block.key === sourceBlockKey);

        if (blockIndex >= 0) {
            // Update sort order
            const draggedBlock = HelperFunctions.getByValue(areaBlocks, 'key', parseInt(draggableId));

            if (draggedBlock) {
                const movedBlocks = insert(blocksToRender, destination.index, draggedBlock).map((_block, index) => {
                    return {
                        ..._block,
                        sortOrder: index,
                    };
                });

                const newAreaBlocks = areaBlocks
                    .map((block) => {
                        const updatedBlock = HelperFunctions.getByValue(movedBlocks, 'key', block.key);
                        const _block = updatedBlock ?? block;

                        if (_block.key === sourceBlockKey) {
                            // Update latest content of block layout
                            _block.latestContent = parseBlockData(newContent);
                            return _block;
                        }

                        return _block;
                    })
                    .sort(HelperFunctions.dynamicSort('sortOrder'));

                replaceBlocks(newAreaBlocks);
            }
        }
    }

    function onDragEnd(result) {
        setDraggedItemKey(undefined);

        const { source, destination } = result;

        if (!destination) {
            return;
        }

        // Dropped on area
        if (destination.droppableId.startsWith('area|')) {
            if (source.droppableId.startsWith('block|')) {
                removeBlockFromLayout(result);
                return;
            }

            moveBlocks(blocksToRender, source.index, destination.index);
            return;
        }

        // Dropped on block layout
        if (destination.droppableId.startsWith('block|')) {
            handleBlockLayoutDrag(result);
            return;
        }
    }

    function moveBlocks(blocks, fromIndex, toIndex) {
        if (toIndex < 0 || toIndex >= blocks.length) {
            return;
        }

        const movedBlocks = move(blocks, fromIndex, toIndex).map((_block, index) => {
            return {
                ..._block,
                sortOrder: index,
            };
        });

        // Update blocks in form with new data
        const newAreaBlocks = areaBlocks
            .map((block) => {
                const updatedBlock = HelperFunctions.getByValue(movedBlocks, 'key', block.key);

                return updatedBlock ?? block;
            })
            .sort(HelperFunctions.dynamicSort('sortOrder'));

        replaceBlocks(newAreaBlocks);
    }
}

function RenderAreaBlocks({ area, blocksToRender = [], isDraggable = true, areaBlocks = [], canAddNewBlocks = true }) {
    const [compareBlocksEnabled] = useQueryParam('compare', BooleanParam);
    const [highlightBlockId] = useQueryParam('highlightBlock', NumberParam);
    const { showHistorySidebar } = useContext(AreaContext);

    return (
        <>
            {blocksToRender.map((block, index) => {
                const isLast = index === blocksToRender.length - 1;

                return (
                    <RenderedBlock
                        area={area}
                        block={block}
                        isLast={isLast}
                        index={index}
                        areaBlocks={areaBlocks}
                        blockIndex={areaBlocks.findIndex((_block) => _block.key === block.key)}
                        isDraggable={isDraggable}
                        canAddNewBlocks={canAddNewBlocks}
                        hideBlockVariants={compareBlocksEnabled || showHistorySidebar}
                        highlight={highlightBlockId === block.key}
                        key={`block-${block.key}-index-${index}`}
                    />
                );
            })}
        </>
    );
}

function RenderedBlock({
    area,
    block,
    isLast,
    blockIndex,
    isDraggable,
    canAddNewBlocks = true,
    index,
    hideBlockVariants = false,
    highlight = false,
    areaBlocks,
}) {
    const isReadOnly = useIsReadOnly(block);
    const markAsDeleted = block.deleted || block.scheduledForDeletion;
    const userTask = useGetBlockApprovalParticipation(block.id, area?.id);

    return (
        <Draggable draggableId={`${block.key}`} index={index} isDragDisabled={!isDraggable || markAsDeleted}>
            {(provided, snapshot) => (
                <div ref={provided.innerRef} {...provided.draggableProps}>
                    <div className="d-flex w-100 position-relative">
                        <div
                            className={cx('edit-area-blocks dr-container-body', {
                                last: isLast,
                                'highlight-block': highlight,
                                'is-dragging-block': snapshot.isDragging,
                                'has-pending-task': userTask !== undefined,
                            })}
                        >
                            {canAddNewBlocks && <InsertBlockField insertIndex={blockIndex} />}

                            <Block
                                isDraggable={isDraggable}
                                isDragging={snapshot.isDragging}
                                block={block}
                                index={blockIndex}
                                readOnly={isReadOnly}
                                dragHandleProps={provided.dragHandleProps}
                                areaBlocks={areaBlocks}
                            />

                            {isLast && canAddNewBlocks && <InsertBlockField insertIndex={blockIndex + 1} />}
                        </div>

                        <div
                            className={cx('edit-area-variants', {
                                'd-none': hideBlockVariants,
                            })}
                        >
                            <BlockVariants area={area} block={block} />
                        </div>
                    </div>
                </div>
            )}
        </Draggable>
    );
}

const getListStyle = (isDraggingOver) => ({
    borderRadius: 5,
    boxShadow: isDraggingOver ? '#00e676 0px 0px 8px 0px' : undefined,
    background: isDraggingOver ? '#00e67614' : undefined,
});

/**
 * Moves an item from one list to another list.
 */
const moveBlockLayout = (source, destination, droppableSource, droppableDestination) => {
    const sourceClone = Array.from(source);
    const destClone = Array.from(destination);
    const [removed] = sourceClone.splice(droppableSource.index, 1);

    destClone.splice(droppableDestination.index, 0, removed);

    return [sourceClone, destClone];
};

const insertBlockLayout = (list, index, item) => {
    const workingCopy = cloneDeep(list);
    workingCopy.splice(index, 0, item);

    return workingCopy;
};

const removeBlockLayout = (list, index) => {
    list.splice(index, 1);
};

const reorderBlockLayout = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
};
